// API Configuration for Event Management System
// Update this file when deploying to different environments

// Environment detection
const isProduction = import.meta.env.PROD;
const isDevelopment = import.meta.env.DEV;

// API Base URLs for different environments
const API_CONFIGS = {
  // Youware platform (default)
  youware: {
    base: 'https://backend.youware.com',
    name: 'Youware Backend'
  },
  
  // Hostinger VPS deployment
  hostinger: {
    base: 'https://your-domain.com', // Replace with your actual domain
    name: 'Hostinger VPS'
  },
  
  // Local development
  local: {
    base: 'http://localhost:3000',
    name: 'Local Development'
  }
};

// ⚠️ DEPLOYMENT CONFIGURATION ⚠️
// Change this when deploying to different platforms:
// - 'youware' for Youware platform
// - 'hostinger' for Hostinger VPS
// - 'local' for local development
const CURRENT_ENVIRONMENT = 'youware'; // 👈 CHANGE THIS FOR DEPLOYMENT

// Get current API configuration
const getCurrentApiConfig = () => {
  const config = API_CONFIGS[CURRENT_ENVIRONMENT as keyof typeof API_CONFIGS];
  
  if (!config) {
    console.warn(`⚠️ Unknown API environment: ${CURRENT_ENVIRONMENT}. Falling back to Youware.`);
    return API_CONFIGS.youware;
  }
  
  return config;
};

// Export API configuration
export const API_CONFIG = getCurrentApiConfig();

console.log(`🔧 API Environment: ${API_CONFIG.name} (${API_CONFIG.base})`);

// API endpoints
export const API_ENDPOINTS = {
  events: `${API_CONFIG.base}/api/events`,
  eventsById: (id: string) => `${API_CONFIG.base}/api/events/${id}`,
  eventsBulk: `${API_CONFIG.base}/api/events/bulk`,
  eventsExport: `${API_CONFIG.base}/api/events/export`,
} as const;

// Environment detection utilities
export const isScreenshotEnvironment = () => {
  return window.location.hostname.includes('paintress') || 
         window.location.hostname.includes('preview') ||
         navigator.userAgent.includes('HeadlessChrome');
};

// API call wrapper with retry logic
export const apiCall = async (url: string, options: RequestInit = {}, maxRetries = 3): Promise<Response> => {
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 API call attempt ${attempt}/${maxRetries} to:`, url);
      
      const response = await fetch(url, {
        ...options,
        // Add timeout
        signal: AbortSignal.timeout(10000), // 10 second timeout
      });
      
      console.log(`📡 Response received (attempt ${attempt}):`, {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        ok: response.ok
      });
      
      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      console.warn(`⚠️ API call failed (attempt ${attempt}/${maxRetries}):`, {
        targetUrl: url,
        error: lastError.message,
        willRetry: attempt < maxRetries
      });
      
      if (attempt < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempt - 1) * 1000;
        console.log(`⏱️ Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // All retries failed
  throw new Error(`API call failed after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`);
};